import type { FC } from "react";

import { isRecord } from "@arti/utilities";

import type { RepositoryIndexingInsight } from "../../types";

import { Badge } from "../common/Badge";
import { Button } from "../common/Button";

function getRecord(
  input: Record<string, unknown> | null,
  path: readonly string[],
): Record<string, unknown> | null {
  let current: unknown = input;
  for (const segment of path) {
    if (!isRecord(current)) {
      return null;
    }
    current = current[segment];
  }
  return isRecord(current) ? current : null;
}

function resolveProfileMetadata(
  metadata: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!metadata) {
    return null;
  }
  const candidate = metadata.profile;
  return isRecord(candidate) ? candidate : metadata;
}

function toStringValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readString(
  input: Record<string, unknown> | null,
  path: readonly string[],
): string | null {
  if (!input || path.length === 0) {
    return null;
  }
  const parent = getRecord(input, path.slice(0, -1));
  if (!parent) {
    return null;
  }
  return toStringValue(parent[path[path.length - 1]]);
}

function readStringList(
  input: Record<string, unknown> | null,
  path: readonly string[],
): string[] {
  if (!input || path.length === 0) {
    return [];
  }
  const parent = getRecord(input, path.slice(0, -1));
  if (!parent) {
    return [];
  }
  const candidate = parent[path[path.length - 1]];
  if (!Array.isArray(candidate)) {
    return [];
  }
  return Array.from(
    new Set(
      candidate
        .map((entry) => toStringValue(entry))
        .filter((entry): entry is string => Boolean(entry)),
    ),
  );
}

function compactList(values: string[], max = 3): string {
  if (values.length <= max) {
    return values.join(" • ");
  }
  return `${values.slice(0, max).join(" • ")} +${values.length - max} more`;
}

const SummaryLine: FC<{ label: string; values: string[] }> = ({
  label,
  values,
}) => {
  if (values.length === 0) {
    return null;
  }
  return (
    <p className="text-xs text-gray-600 dark:text-white/65">
      <span className="font-semibold text-gray-700 dark:text-white/80">
        {label}:
      </span>{" "}
      {compactList(values)}
    </p>
  );
};

export const RepositoryIdentityInsightSummary: FC<{
  insight: RepositoryIndexingInsight;
  onOpenProfile: () => void;
}> = ({ insight, onOpenProfile }) => {
  const metadata = resolveProfileMetadata(
    isRecord(insight.metadata) ? insight.metadata : null,
  );
  const role = readString(metadata, ["identity", "repository_role"]);
  const defaultBranch = readString(metadata, ["development", "default_branch"]);
  const responsibilities = readStringList(metadata, [
    "identity",
    "responsibilities",
  ]);
  const patterns = readStringList(metadata, ["architecture", "patterns"]);
  const providers = readStringList(metadata, ["integrations", "providers"]);

  const badges = [
    insight.category ? `Domain: ${insight.category}` : null,
    role ? `Role: ${role}` : null,
    insight.primary_language ? `Language: ${insight.primary_language}` : null,
    defaultBranch ? `Branch: ${defaultBranch}` : null,
  ].filter((value): value is string => Boolean(value));

  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3 dark:border-white/5">
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {badges.map((badge) => (
            <Badge key={badge} variant="default" size="sm">
              {badge}
            </Badge>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <SummaryLine label="Responsibilities" values={responsibilities} />
        <SummaryLine label="Architecture" values={patterns} />
        <SummaryLine label="Integrations" values={providers} />
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={onOpenProfile}>
          View Full Profile
        </Button>
      </div>
    </div>
  );
};
